/**
 * Pure, framework-free helpers for the embed search widget's filter state.
 * Kept separate from the component so filter-building/counting logic is
 * unit-testable without pulling in React Testing Library (not used in this
 * repo - see src/helpers/*.test.js for the existing pattern).
 */

// Groups that narrow the feed via a multi-select id list (server side these
// become `financeType`, `timeline`, `purchaseType`, `propertyType` - comma
// joined lists of ids, same convention as parseIntListParam on the API).
export const FILTER_GROUPS = [
  "propertyType",
  "financeType",
  "timeline",
  "purchaseType",
];

// A fresh, empty selection - one Set per group, nothing pre-selected.
export function createEmptyWidgetFilterState() {
  return {
    search: "",
    sortType: "newest",
    preApproved: false,
    selected: FILTER_GROUPS.reduce((acc, group) => {
      acc[group] = new Set();
      return acc;
    }, {}),
  };
}

/**
 * Flattens the widget's filter state into the flat query-param object the
 * public widget feed endpoint expects. Empty groups/search are omitted
 * entirely rather than sent as empty strings, so a widget with nothing
 * selected sends exactly the same request shape the plain list view does.
 */
export function buildWidgetSearchQuery(state, { page = 1, limit } = {}) {
  const params = {};

  const search = (state?.search || "").trim();
  if (search) params.search = search;

  if (state?.sortType) params.sortType = state.sortType;

  FILTER_GROUPS.forEach((group) => {
    const set = state?.selected?.[group];
    if (set && set.size > 0) {
      params[group] = Array.from(set).join(",");
    }
  });

  if (state?.preApproved) params.preApproved = "true";

  params.page = page;
  if (limit) params.limit = limit;

  return params;
}

/**
 * Number of filter groups actively narrowing the feed, for the "Filters (n)"
 * toggle badge. Search has its own visible input and isn't counted here -
 * mirrors src/helpers/activeFilterCount.js not double-counting the keyword
 * box against the "Filters" affordance.
 */
export function computeActiveWidgetFilterCount(state) {
  let count = 0;
  FILTER_GROUPS.forEach((group) => {
    const set = state?.selected?.[group];
    if (set && set.size > 0) count += 1;
  });
  if (state?.preApproved) count += 1;
  return count;
}

/**
 * Toggles a single value in/out of a filter group's Set, returning a new
 * state object (never mutates the input) so it drops straight into
 * useState's updater form.
 */
export function toggleWidgetFilterValue(state, group, value) {
  const current = state?.selected?.[group] || new Set();
  const next = new Set(current);
  next.has(value) ? next.delete(value) : next.add(value);
  return {
    ...state,
    selected: { ...state.selected, [group]: next },
  };
}
